import { reverseGeocoding } from '../utils/geoCoding';
import { GeocodingCache } from './geocoding-cache.service';

interface QueueItem {
  coords: { lat: number; lng: number };
  resolve: (address: string) => void;
  reject: (error: any) => void;
  priority?: number;
}

export class GeocodingService {
  private static instance: GeocodingService;
  private queue: QueueItem[] = [];
  private processing = false;
  private requestCount = 0;
  private resetTime = Date.now() + 86400000; // 24h
  private readonly DAILY_LIMIT = 2500; // Google Maps free tier
  private readonly BATCH_SIZE = 5;
  private readonly REQUEST_DELAY = 200; // ms between requests
  private readonly BATCH_DELAY = 1000; // ms between batches

  private constructor() {
    // Load request count from localStorage
    this.loadRequestCount();
  }

  static getInstance(): GeocodingService {
    if (!this.instance) {
      this.instance = new GeocodingService();
    }
    return this.instance;
  }

  /**
   * Get address for coordinates with caching and rate limiting
   */
  async getAddress(latitude: number, longitude: number, priority: number = 0): Promise<string> {
    // Validate coordinates
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      return 'Coordonnées invalides';
    }

    // Check cache first
    const cached = GeocodingCache.get(latitude, longitude);
    if (cached) {
      return cached;
    }

    // Check if we've hit the daily limit
    if (this.requestCount >= this.DAILY_LIMIT) {
      // Try to return a basic approximation
      return this.getApproximateLocation(latitude, longitude);
    }

    // Add to queue
    return new Promise((resolve, reject) => {
      this.queue.push({ 
        coords: { lat: latitude, lng: longitude }, 
        resolve, 
        reject,
        priority 
      });
      
      // Sort by priority (higher priority first)
      this.queue.sort((a, b) => (b.priority || 0) - (a.priority || 0));
      
      // Start processing if not already
      this.processQueue();
    });
  }

  /**
   * Process the geocoding queue with rate limiting
   */
  private async processQueue() {
    if (this.processing || this.queue.length === 0) return;

    this.processing = true;

    try {
      // Reset counter if 24h have passed
      if (Date.now() > this.resetTime) {
        this.requestCount = 0;
        this.resetTime = Date.now() + 86400000;
        this.saveRequestCount();
      }

      // Process a batch
      const batch = this.queue.splice(0, this.BATCH_SIZE);
      
      // Group by similar coordinates to reduce duplicate requests
      const uniqueRequests = this.groupSimilarCoordinates(batch);

      for (const group of uniqueRequests) {
        const { coords, items } = group;
        
        try {
          // Check cache again (might have been added by another request)
          const cached = GeocodingCache.get(coords.lat, coords.lng);
          if (cached) {
            items.forEach(item => item.resolve(cached));
            continue;
          }

          // Check daily limit
          if (this.requestCount >= this.DAILY_LIMIT) {
            const fallback = this.getApproximateLocation(coords.lat, coords.lng);
            items.forEach(item => item.resolve(fallback));
            continue;
          }

          // Make the API call
          const result = await reverseGeocoding(coords.lat, coords.lng);
          const address = this.formatAddress(result);

          // Cache the result
          GeocodingCache.set(coords.lat, coords.lng, address);

          // Increment counter and save
          this.requestCount++;
          this.saveRequestCount();

          // Resolve all items in this group
          items.forEach(item => item.resolve(address));

          // Rate limiting delay
          if (this.queue.length > 0) {
            await this.delay(this.REQUEST_DELAY);
          }
        } catch (error) {
          console.error('Geocoding error:', error);
          const fallback = this.getApproximateLocation(coords.lat, coords.lng);
          items.forEach(item => item.resolve(fallback));
        }
      }
    } finally {
      this.processing = false;

      // Continue processing if queue not empty
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), this.BATCH_DELAY);
      }
    }
  }

  /**
   * Group similar coordinates to avoid duplicate requests
   */
  private groupSimilarCoordinates(items: QueueItem[]): Array<{
    coords: { lat: number; lng: number };
    items: QueueItem[];
  }> {
    const groups = new Map<string, { coords: { lat: number; lng: number }; items: QueueItem[] }>();

    items.forEach(item => {
      // Round to 6 decimal places for grouping
      const key = `${item.coords.lat.toFixed(6)},${item.coords.lng.toFixed(6)}`;
      
      if (!groups.has(key)) {
        groups.set(key, {
          coords: item.coords,
          items: []
        });
      }
      
      groups.get(key)!.items.push(item);
    });

    return Array.from(groups.values());
  }

  /**
   * Format address from geocoding result
   */
  private formatAddress(result: any): string {
    if (!result) {
      return 'Adresse inconnue';
    }

    // Priority: city > formatted short address > full address
    if (result.city && result.postalCode) {
      return `${result.city} (${result.postalCode})`;
    } else if (result.city) {
      return result.city;
    } else if (result.address) {
      // Try to extract a shorter version
      const parts = result.address.split(',');
      if (parts.length > 2) {
        return parts.slice(0, 2).join(',').trim();
      }
      return result.address;
    }

    return 'Adresse inconnue';
  }

  /**
   * Get approximate location without API call
   */
  private getApproximateLocation(lat: number, lng: number): string {
    // France regions approximation
    if (lat >= 48.5 && lat <= 49 && lng >= 2 && lng <= 2.8) {
      return 'Région Parisienne';
    } else if (lat >= 43 && lat <= 43.5 && lng >= 5 && lng <= 5.5) {
      return 'Région Marseille';
    } else if (lat >= 45.7 && lat <= 45.8 && lng >= 4.8 && lng <= 4.9) {
      return 'Région Lyon';
    } else if (lat >= 43.5 && lat <= 43.7 && lng >= 1.3 && lng <= 1.5) {
      return 'Région Toulouse';
    } else if (lat >= 47.1 && lat <= 47.3 && lng >= -1.6 && lng <= -1.5) {
      return 'Région Nantes';
    } else if (lat >= 41 && lat <= 51 && lng >= -5 && lng <= 10) {
      return 'France';
    }
    
    return `${lat.toFixed(3)}, ${lng.toFixed(3)}`;
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Load request count from localStorage
   */
  private loadRequestCount(): void {
    try {
      const data = localStorage.getItem('geocoding_request_count');
      if (data) {
        const parsed = JSON.parse(data);
        if (parsed.resetTime > Date.now()) {
          this.requestCount = parsed.count || 0;
          this.resetTime = parsed.resetTime;
        }
      }
    } catch (error) {
      console.error('Error loading request count:', error);
    }
  }

  /**
   * Save request count to localStorage
   */
  private saveRequestCount(): void {
    try {
      localStorage.setItem('geocoding_request_count', JSON.stringify({
        count: this.requestCount,
        resetTime: this.resetTime
      }));
    } catch (error) {
      console.error('Error saving request count:', error);
    }
  }

  /**
   * Get service statistics
   */
  getStats(): {
    queueLength: number;
    requestCount: number;
    dailyLimit: number;
    remainingRequests: number;
    resetTime: Date;
    cacheStats: any;
  } {
    return {
      queueLength: this.queue.length,
      requestCount: this.requestCount,
      dailyLimit: this.DAILY_LIMIT,
      remainingRequests: Math.max(0, this.DAILY_LIMIT - this.requestCount),
      resetTime: new Date(this.resetTime),
      cacheStats: GeocodingCache.getStats()
    };
  }

  /**
   * Clear the queue
   */
  clearQueue(): void {
    this.queue.forEach(item => {
      item.resolve('Requête annulée');
    });
    this.queue = [];
  }

  /**
   * Reset daily counter (for testing)
   */
  resetDailyCounter(): void {
    this.requestCount = 0;
    this.resetTime = Date.now() + 86400000;
    this.saveRequestCount();
  }
}